import medianIncomeData from '../json/median_income.json';
import expenseBreakdown from '../json/expense_breakdown.json';
import outOfPocketHealth from '../json/out_of_pocket_health.json';
import housingExpenses from '../json/housing_expenses.json';
import regionalTransportation from '../json/regional_transportation_expenses.json';
import administrativeExpenses from '../json/nested_county_district_admin_expense.json';

export const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
  OR: 'Oregon', PA: 'Pennsylvania', PR: 'Puerto Rico', RI: 'Rhode Island',
  SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas',
  UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia',
  WI: 'Wisconsin', WY: 'Wyoming',
};

// Official Form 122A-2 (04/25), line 40. These amounts apply to cases filed
// on or after April 1, 2025 and are next subject to adjustment April 1, 2028.
export const CHAPTER_7_LINE_40_LOWER_LIMIT = 10275;
export const CHAPTER_7_LINE_40_UPPER_LIMIT = 17150;

const n = value => {
  const parsed = Number(String(value ?? '').replace(/[$,]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const calculateChapter7PresumptionResult = (line39dValue, line41bValue) => {
  const line39d = n(line39dValue);
  const line41b = n(line41bValue);

  if (line39d < CHAPTER_7_LINE_40_LOWER_LIMIT) {
    return {
      chapter7Line40Category: 'below_range',
      chapter7UsesDebtTest: false,
      chapter7Line42Result: 'not_applicable',
      chapter7Result: 'no_presumption_low',
      chapter7Presumption: false,
    };
  }

  if (line39d > CHAPTER_7_LINE_40_UPPER_LIMIT) {
    return {
      chapter7Line40Category: 'above_range',
      chapter7UsesDebtTest: false,
      chapter7Line42Result: 'not_applicable',
      chapter7Result: 'presumption_high',
      chapter7Presumption: true,
    };
  }

  const chapter7Presumption = line39d >= line41b;
  return {
    chapter7Line40Category: 'within_range',
    chapter7UsesDebtTest: true,
    chapter7Line42Result: chapter7Presumption ? 'presumption' : 'no_presumption',
    chapter7Result: chapter7Presumption
      ? 'presumption_debt_test'
      : 'no_presumption_debt_test',
    chapter7Presumption,
  };
};

const sameText = (left, right) => {
  const a = String(left || '').toLowerCase().replace(/\bcounty\b/g, '').trim();
  const b = String(right || '').toLowerCase().replace(/\bcounty\b/g, '').trim();
  return !!a && !!b && (a.includes(b) || b.includes(a));
};

const monthlyIncomeTotal = income => {
  const fields = [
    'gross_wages', 'alimony', 'household_expenses', 'biz_net_monthly',
    'real_net_monthly', 'interest_dividends', 'unemployment', 'pension',
  ];
  return fields.reduce((total, key) => total + n(income?.[key]), 0)
    + (income?.other_incomes || []).reduce((total, item) => total + n(item.amount), 0);
};

const ageOnDate = value => {
  if (!value) return 30;
  const dob = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(dob.getTime())) return 30;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const beforeBirthday = today.getMonth() < dob.getMonth()
    || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate());
  if (beforeBirthday) age -= 1;
  return age;
};

const expenseTotal = (expenses, categories) => (expenses || [])
  .filter(item => categories.includes(item.category))
  .reduce((total, item) => total + n(item.amount), 0);

const deductionTotal = (caseData, types) => (caseData?.debtors || [])
  .flatMap(debtor => debtor.employments || [])
  .flatMap(employment => employment.deductions || [])
  .filter(item => types.includes(item.deduction_type))
  .reduce((total, item) => total + n(item.amount), 0);

const assetForLink = (caseData, link) => {
  const id = String(link?.object_id || '');
  const type = String(link?.asset_type || '').toLowerCase();
  if (type === 'realproperty') {
    return (caseData?.real_properties || []).find(item => String(item.id) === id) || null;
  }
  if (type === 'vehicle') {
    return (caseData?.vehicles || []).find(item => String(item.id) === id) || null;
  }
  return null;
};

const propertyLabel = (asset, link, creditor) => {
  if (link?.asset_label) return link.asset_label;
  if (asset?.street1) return [asset.street1, asset.city].filter(Boolean).join(', ');
  if (asset?.make || asset?.model) return [asset.year, asset.make, asset.model].filter(Boolean).join(' ');
  return creditor.account_type || creditor.lien_description || 'Secured property';
};

export const suggestedSecuredDebts = (caseData, creditors) => {
  const vehicles = (caseData?.vehicles || []).filter(vehicle => !vehicle.is_recreational);
  return (creditors || [])
    .filter(creditor => creditor.debt_type === 'secured')
    .map(creditor => {
      const link = creditor.asset_links?.[0] || null;
      const asset = assetForLink(caseData, link);
      const type = String(link?.asset_type || '').toLowerCase();
      const vehicleIndex = type === 'vehicle'
        ? vehicles.findIndex(item => String(item.id) === String(link?.object_id))
        : -1;
      let category = 'other';
      if (type === 'realproperty' && asset?.principal_residence) category = 'home';
      if (type === 'vehicle' && vehicleIndex === 0) category = 'vehicle1';
      if (type === 'vehicle' && vehicleIndex === 1) category = 'vehicle2';

      const isSecondMortgage = /second|2nd/i.test(creditor.account_type || '');
      const monthlyPayment = isSecondMortgage
        ? n(asset?.second_monthly_payment) || n(asset?.monthly_payment)
        : n(asset?.monthly_payment) || n(link?.monthly_payment);

      return {
        source_creditor_id: creditor.id,
        category,
        creditor_name: creditor.account_name || '',
        property_description: propertyLabel(asset, link, creditor),
        average_monthly_payment: monthlyPayment,
        includes_taxes_insurance: !!creditor.piti,
        cure_total: n(creditor.arrearage),
      };
    });
};

export const suggestedPriorityClaimTotal = creditors => (creditors || [])
  .filter(creditor => creditor.debt_type === 'priority')
  .reduce((total, creditor) => total + n(creditor.priority_amount || creditor.balance), 0);

export function suggestedRentHousingAdjustment({
  caseData,
  dependents = [],
  expenses = [],
  securedDebts = [],
  householdSize: householdSizeOverride,
}) {
  const householdSize = Math.max(
    1,
    n(householdSizeOverride) || (caseData?.debtors?.length || 0) + dependents.length
  );
  const state = caseData?.debtors?.[0]?.state || '';
  const county = caseData?.debtors?.[0]?.county || '';
  const mortgageRentStandard = housingStandard(state, county, householdSize).mortgage;
  const actualRent = expenseTotal(expenses, ['rent_mortgage']);
  const hasHomeMortgage = securedDebts.some(item => item.category === 'home');
  const amount = hasHomeMortgage ? 0 : Math.max(0, actualRent - mortgageRentStandard);

  return {
    actualRent,
    mortgageRentStandard,
    hasHomeMortgage,
    amount,
    explanation: amount > 0 ? 'actual rent difference' : '',
  };
}

export const createDefaultLongFormData = ({ caseData, dependents = [], expenses = [], creditors = [] }) => {
  const householdSize = Math.max(1, (caseData?.debtors?.length || 0) + dependents.length);
  const ages = [
    ...(caseData?.debtors || []).map(debtor => ageOnDate(debtor.dob)),
    ...dependents.map(dependent => n(dependent.age)),
  ];
  const under65 = ages.filter(age => age < 65).length;
  const over65 = Math.max(0, householdSize - under65);
  const standardHealth = (under65 * n(outOfPocketHealth.under_65))
    + (over65 * n(outOfPocketHealth['65_and_older']));
  const actualMedical = expenseTotal(expenses, ['medical']);
  const taxes = deductionTotal(caseData, ['taxes']) || expenseTotal(expenses, ['taxes']);
  const involuntary = deductionTotal(caseData, ['mandatory_retirement', 'union_dues']);
  const payrollDSO = deductionTotal(caseData, ['dso']);
  const scheduledDSO = expenseTotal(expenses, ['alimony_child_support']);
  const payrollInsurance = deductionTotal(caseData, ['insurance']);
  const scheduledInsurance = expenseTotal(expenses, ['health_insurance']);
  const securedDebts = suggestedSecuredDebts(caseData, creditors);
  const rentAdjustment = suggestedRentHousingAdjustment({
    caseData,
    dependents,
    expenses,
    securedDebts,
  });

  return {
    version: 1,
    household_size: householdSize,
    vehicle_count: Math.min(2, (caseData?.vehicles || []).filter(vehicle => !vehicle.is_recreational).length),
    housing_adjustment_explanation: rentAdjustment.explanation,
    housing_adjustment_amount: rentAdjustment.amount,
    housing_adjustment_source: 'rent_difference',
    additional_public_transportation: 0,
    taxes,
    involuntary_deductions: involuntary,
    life_insurance: expenseTotal(expenses, ['life_insurance']),
    court_ordered_payments: Math.max(payrollDSO, scheduledDSO),
    education: 0,
    childcare: expenseTotal(expenses, ['childcare']),
    additional_health_care: Math.max(0, actualMedical - standardHealth),
    optional_telephones: 0,
    health_insurance: Math.max(payrollInsurance, scheduledInsurance),
    disability_insurance: 0,
    health_savings_account: 0,
    actually_spend_health_total: true,
    actual_health_total: Math.max(payrollInsurance, scheduledInsurance),
    family_support_contributions: expenseTotal(expenses, ['support_other']),
    family_violence: 0,
    additional_home_energy: 0,
    dependent_education: 0,
    additional_food_clothing: 0,
    charitable_contributions: expenseTotal(expenses, ['charitable']),
    secured_debts: securedDebts,
    priority_claim_total: suggestedPriorityClaimTotal(creditors),
    eligible_for_chapter_13: true,
    projected_chapter_13_payment: 0,
    unsecured_nonpriority_total: (creditors || [])
      .filter(creditor => creditor.debt_type === 'unsecured')
      .reduce((total, creditor) => total + n(creditor.balance), 0),
    dependent_child_income: 0,
    retirement_contributions_and_loans: deductionTotal(caseData, ['voluntary_retirement', 'retirement_loan']),
    special_circumstances: [],
    income_expense_changes: [],
  };
};

export const mergeLongFormData = (saved, defaults) => {
  const hasSavedHousingSource = !!saved
    && Object.prototype.hasOwnProperty.call(saved, 'housing_adjustment_source');
  const legacyRentDifference = !hasSavedHousingSource
    && /^actual rent difference$/i.test(String(saved?.housing_adjustment_explanation || '').trim());
  const legacyUntouchedHousing = !hasSavedHousingSource
    && n(saved?.housing_adjustment_amount) === 0
    && !String(saved?.housing_adjustment_explanation || '').trim();
  const useSuggestedHousing = !saved
    || saved.housing_adjustment_source === 'rent_difference'
    || legacyRentDifference
    || legacyUntouchedHousing;

  return {
    ...defaults,
    ...(saved || {}),
    housing_adjustment_amount: useSuggestedHousing
      ? defaults.housing_adjustment_amount
      : saved.housing_adjustment_amount,
    housing_adjustment_explanation: useSuggestedHousing
      ? defaults.housing_adjustment_explanation
      : saved.housing_adjustment_explanation,
    housing_adjustment_source: useSuggestedHousing
      ? defaults.housing_adjustment_source
      : (saved.housing_adjustment_source || 'manual'),
    secured_debts: Array.isArray(saved?.secured_debts) ? saved.secured_debts : defaults.secured_debts,
    special_circumstances: Array.isArray(saved?.special_circumstances) ? saved.special_circumstances : [],
    income_expense_changes: Array.isArray(saved?.income_expense_changes) ? saved.income_expense_changes : [],
  };
};

function housingStandard(state, county, householdSize) {
  const entries = housingExpenses[state] || [];
  const match = entries.find(item => sameText(item.county, county)) || entries[0] || {};
  const values = match.expenses?.[String(Math.min(5, Math.max(1, householdSize)))] || {};
  return {
    nonMortgage: n(values.non_mortgage),
    mortgage: n(values.mortgage),
  };
}

const transportationStandards = (state, county, vehicleCount) => {
  const stateName = STATE_NAMES[state] || state;
  const region = Object.values(regionalTransportation).find(item =>
    (item.states || []).includes(stateName)
  );
  if (!region) return { operating: 0, ownership1: 0, ownership2: 0, publicTransportation: 0 };

  const vehicleKey = vehicleCount >= 2 ? 'two_cars' : 'one_car';
  const msa = Object.values(region.operating_costs?.msa || {}).find(item =>
    (item.counties || []).some(itemCounty => sameText(itemCounty, county))
  );
  const operating = vehicleCount > 0
    ? n(msa?.[vehicleKey] ?? region.operating_costs?.region?.[vehicleKey])
    : 0;
  const ownershipOneCar = n(region.ownership_costs?.one_car);
  const ownershipTwoCars = n(region.ownership_costs?.two_cars);
  return {
    operating,
    ownership1: vehicleCount >= 1 ? ownershipOneCar : 0,
    ownership2: vehicleCount >= 2 ? Math.max(0, ownershipTwoCars - ownershipOneCar) : 0,
    publicTransportation: n(region.public_transportation),
  };
};

const administrativeMultiplier = (state, county) => {
  const stateName = STATE_NAMES[state] || state;
  const districts = administrativeExpenses[stateName] || {};
  const match = Object.values(districts).find(item => {
    const counties = item.counties || [];
    return counties.some(itemCounty => /all counties/i.test(itemCounty) || sameText(itemCounty, county));
  });
  return n(match?.admin_expense_multiplier);
};

export const calculateLongFormMeansTest = ({
  caseData,
  dependents = [],
  meansTestIncomes = [],
  maritalAdjustments = [],
  longFormData = {},
}) => {
  const householdSize = Math.max(1, n(longFormData.household_size)
    || (caseData?.debtors?.length || 0) + dependents.length);
  const state = caseData?.debtors?.[0]?.state || '';
  const county = caseData?.debtors?.[0]?.county || '';
  const ages = [
    ...(caseData?.debtors || []).map(debtor => ageOnDate(debtor.dob)),
    ...dependents.map(dependent => n(dependent.age)),
  ];
  while (ages.length < householdSize) ages.push(30);
  const under65 = ages.slice(0, householdSize).filter(age => age < 65).length;
  const over65 = Math.max(0, householdSize - under65);

  const foodCategories = ['Food', 'Housekeeping supplies', 'Apparel & services', 'Personal care products & services', 'Miscellaneous'];
  const line6 = foodCategories.reduce(
    (total, category) => total + n(expenseBreakdown[category]?.[String(householdSize)]),
    0
  );
  const foodAndApparel = n(expenseBreakdown.Food?.[String(householdSize)])
    + n(expenseBreakdown['Apparel & services']?.[String(householdSize)]);
  const healthUnder65 = n(outOfPocketHealth.under_65);
  const healthOver65 = n(outOfPocketHealth['65_and_older']);
  const line7c = healthUnder65 * under65;
  const line7f = healthOver65 * over65;
  const line7g = line7c + line7f;
  const housing = housingStandard(state, county, householdSize);

  const secured = longFormData.secured_debts || [];
  const homeDebts = secured.filter(item => item.category === 'home');
  const vehicle1Debts = secured.filter(item => item.category === 'vehicle1');
  const vehicle2Debts = secured.filter(item => item.category === 'vehicle2');
  const otherSecuredDebts = secured.filter(item => item.category === 'other');
  const inferredVehicleCount = Math.min(2, (caseData?.vehicles || []).filter(vehicle => !vehicle.is_recreational).length);
  const hasVehicleOverride = longFormData.vehicle_count !== undefined
    && longFormData.vehicle_count !== null
    && longFormData.vehicle_count !== '';
  const requestedVehicleCount = hasVehicleOverride
    ? Math.min(2, Math.max(0, Math.trunc(n(longFormData.vehicle_count))))
    : inferredVehicleCount;
  const securedDebtVehicleCount = vehicle2Debts.length ? 2 : vehicle1Debts.length ? 1 : 0;
  const vehicleCount = Math.max(requestedVehicleCount, securedDebtVehicleCount);
  const transportation = transportationStandards(state, county, vehicleCount);
  const sumMonthly = rows => rows.reduce((total, item) => total + n(item.average_monthly_payment), 0);
  const line9b = sumMonthly(homeDebts);
  const line9c = Math.max(0, housing.mortgage - line9b);
  const line10 = homeDebts.length && longFormData.housing_adjustment_source === 'rent_difference'
    ? 0
    : n(longFormData.housing_adjustment_amount);
  const line12 = transportation.operating;
  const line13b = sumMonthly(vehicle1Debts);
  const line13a = line13b > 0 ? transportation.ownership1 : 0;
  const line13c = Math.max(0, line13a - line13b);
  const line13e = sumMonthly(vehicle2Debts);
  const line13d = line13e > 0 ? transportation.ownership2 : 0;
  const line13f = Math.max(0, line13d - line13e);
  const line14 = vehicleCount === 0 ? transportation.publicTransportation : 0;
  const line15 = Math.min(
    n(longFormData.additional_public_transportation),
    transportation.publicTransportation
  );

  const otherNecessary = [
    'taxes', 'involuntary_deductions', 'life_insurance', 'court_ordered_payments',
    'education', 'childcare', 'additional_health_care', 'optional_telephones',
  ].reduce((total, key) => total + n(longFormData[key]), 0);
  const line24 = line6 + line7g + housing.nonMortgage + line9c + line10 + line12
    + line13c + line13f + line14 + line15 + otherNecessary;

  const healthInsuranceTotal = n(longFormData.health_insurance)
    + n(longFormData.disability_insurance)
    + n(longFormData.health_savings_account);
  const line25 = longFormData.actually_spend_health_total === false
    ? n(longFormData.actual_health_total)
    : healthInsuranceTotal;
  const line30Maximum = foodAndApparel * 0.05;
  const line30 = Math.min(n(longFormData.additional_food_clothing), line30Maximum);
  const line32 = line25
    + n(longFormData.family_support_contributions)
    + n(longFormData.family_violence)
    + n(longFormData.additional_home_energy)
    + n(longFormData.dependent_education)
    + line30
    + n(longFormData.charitable_contributions);

  const line33d = sumMonthly(otherSecuredDebts);
  const line33e = line9b + line13b + line13e + line33d;
  const cureTotal = secured.reduce((total, item) => total + n(item.cure_total), 0);
  const line34 = cureTotal / 60;
  const priorityClaimTotal = n(longFormData.priority_claim_total);
  const line35 = priorityClaimTotal / 60;
  const adminMultiplier = administrativeMultiplier(state, county);
  const chapter = String(caseData?.chapter || '');
  const includeChapter13AdminExpense = chapter === '13'
    || longFormData.eligible_for_chapter_13 !== false;
  const line36 = includeChapter13AdminExpense
    ? n(longFormData.projected_chapter_13_payment) * (adminMultiplier / 100)
    : 0;
  const line37 = line33e + line34 + line35 + line36;
  const line38 = line24 + line32 + line37;

  const combinedMonthly = meansTestIncomes.reduce((total, income) => total + monthlyIncomeTotal(income), 0);
  const maritalAdjustment = maritalAdjustments.reduce((total, item) => total + n(item.amount), 0);
  const adjustedMonthlyIncome = Math.max(0, combinedMonthly - maritalAdjustment);
  const medianIncome = n(medianIncomeData[STATE_NAMES[state]]?.[String(householdSize)]);
  const aboveMedian = adjustedMonthlyIncome * 12 > medianIncome;
  const line39c = adjustedMonthlyIncome - line38;
  const line39d = line39c * 60;
  const unsecuredTotal = n(longFormData.unsecured_nonpriority_total);
  const line41b = unsecuredTotal * 0.25;
  const chapter7Determination = calculateChapter7PresumptionResult(line39d, line41b);

  const specialCircumstancesTotal = (longFormData.special_circumstances || [])
    .reduce((total, item) => total + n(item.amount), 0);
  const chapter13Adjustments = n(longFormData.dependent_child_income)
    + n(longFormData.retirement_contributions_and_loans)
    + line38
    + specialCircumstancesTotal;

  return {
    householdSize, state, county, ages, under65, over65, medianIncome, aboveMedian,
    combinedMonthly, maritalAdjustment, adjustedMonthlyIncome,
    vehicleCount, inferredVehicleCount, requestedVehicleCount,
    vehicleCountRaisedByDebt: vehicleCount > requestedVehicleCount,
    transportation, housing, adminMultiplier,
    homeDebts, vehicle1Debts, vehicle2Debts, otherSecuredDebts,
    line6, foodAndApparel, healthUnder65, healthOver65, line7c, line7f, line7g,
    line8: housing.nonMortgage, line9a: housing.mortgage, line9b, line9c, line10,
    line12, line13a, line13b, line13c,
    line13d, line13e, line13f, line14, line15,
    line24, healthInsuranceTotal, line25, line30Maximum, line30, line32,
    line33d, line33e, cureTotal, line34, priorityClaimTotal, line35, line36, line37, line38,
    line39c, line39d, unsecuredTotal, line41b,
    line40LowerLimit: CHAPTER_7_LINE_40_LOWER_LIMIT,
    line40UpperLimit: CHAPTER_7_LINE_40_UPPER_LIMIT,
    ...chapter7Determination,
    specialCircumstancesTotal,
    chapter13Adjustments,
    chapter13DisposableIncome: adjustedMonthlyIncome - chapter13Adjustments,
  };
};

export const longFormMoney = value => n(value).toFixed(2);
